/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.sessions

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.ui.CauceDateField
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.feature.sessions.generated.resources.Res
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_date_label
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_fee_label
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_patient_label
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_save
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_type_double
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_type_final
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_type_first
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_type_label
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_type_regular

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SessionFormContent(
    state: LogSessionState,
    onAction: (LogSessionAction) -> Unit,
    dateFormatter: DateFormatter,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState()),
    ) {
        Spacer(modifier = Modifier.height(8.dp))

        var patientExpanded by remember { mutableStateOf(false) }
        val selectedPatient = state.patients.find { it.id == state.selectedPatientId }

        ExposedDropdownMenuBox(
            expanded = patientExpanded,
            onExpandedChange = { patientExpanded = it },
        ) {
            OutlinedTextField(
                value = if (state.isLoadingPatients) "" else selectedPatient?.name ?: "",
                onValueChange = {},
                readOnly = true,
                enabled = !state.isLoadingPatients,
                label = { Text(stringResource(Res.string.sessions_patient_label)) },
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = patientExpanded) },
                modifier = Modifier.fillMaxWidth()
                    .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
            )
            ExposedDropdownMenu(
                expanded = patientExpanded,
                onDismissRequest = { patientExpanded = false },
            ) {
                state.patients.forEach { patient ->
                    DropdownMenuItem(
                        text = { Text(patient.name) },
                        onClick = {
                            onAction(LogSessionAction.PatientSelected(patient.id))
                            patientExpanded = false
                        },
                    )
                }
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        CauceDateField(
            selectedDate = state.sessionDate,
            onDateSelected = { onAction(LogSessionAction.DateChanged(it)) },
            dateFormatter = dateFormatter,
            label = { Text(stringResource(Res.string.sessions_date_label)) },
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(12.dp))

        OutlinedTextField(
            value = state.feeInput,
            onValueChange = { onAction(LogSessionAction.FeeChanged(it)) },
            label = { Text(stringResource(Res.string.sessions_fee_label)) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(12.dp))

        var typeExpanded by remember { mutableStateOf(false) }
        val sessionTypeLabels = mapOf(
            "REGULAR" to stringResource(Res.string.sessions_type_regular),
            "FIRST" to stringResource(Res.string.sessions_type_first),
            "FINAL" to stringResource(Res.string.sessions_type_final),
            "DOUBLE" to stringResource(Res.string.sessions_type_double),
        )

        ExposedDropdownMenuBox(
            expanded = typeExpanded,
            onExpandedChange = { typeExpanded = it },
        ) {
            OutlinedTextField(
                value = sessionTypeLabels[state.sessionType] ?: state.sessionType,
                onValueChange = {},
                readOnly = true,
                label = { Text(stringResource(Res.string.sessions_type_label)) },
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = typeExpanded) },
                modifier = Modifier.fillMaxWidth()
                    .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
            )
            ExposedDropdownMenu(
                expanded = typeExpanded,
                onDismissRequest = { typeExpanded = false },
            ) {
                sessionTypeLabels.forEach { (key, label) ->
                    DropdownMenuItem(
                        text = { Text(label) },
                        onClick = {
                            onAction(LogSessionAction.TypeChanged(key))
                            typeExpanded = false
                        },
                    )
                }
            }
        }

        if (state.error != null) {
            Spacer(modifier = Modifier.height(8.dp))
            Text(state.error.asString(), color = MaterialTheme.colorScheme.error)
        }

        Spacer(modifier = Modifier.height(24.dp))

        Button(
            onClick = { onAction(LogSessionAction.SaveClicked) },
            enabled = !state.isSaving,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text(stringResource(Res.string.sessions_save))
        }

        Spacer(modifier = Modifier.height(16.dp))
    }
}
